import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  QueryList,
  ViewChildren,
  ViewChild,
  WritableSignal,
  signal,
} from '@angular/core';
import { twJoin } from 'tailwind-merge';
import { Router } from '@angular/router';
import { A11yService } from '@app/shared/services/a11y.service';

export interface TabConfig {
  name: string;
  routerLink?: string | null;
  isHighlighted?: boolean;
  id: string;
  panelId?: string;
}

export interface TabsConfiguration {
  enableHighlightIndicators?: boolean;
  tabs: TabConfig[];
}
/**
 * Component with multiple tabs which the user can toggle betwee.
 * each tab has a 'isHighlighted' flag which can be used to indicate unsaved state
 * set 'enableHighlightIndicators' to true to enable tab highlight
 */
@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'da-tabs',
  standalone: true,
  templateUrl: './tabs.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TabsComponent implements OnInit {
  @Input() public configuration!: WritableSignal<TabsConfiguration>;
  @Input() public activeTab?: WritableSignal<TabConfig | null> = signal(null);
  @Output() public tabChange = new EventEmitter<TabConfig | null>();
  @ViewChildren('tabButton') public tabButtons: QueryList<
    ElementRef<HTMLButtonElement>
  >;
  @ViewChild('tabBar') public tabBar: ElementRef<HTMLElement>;
  @ViewChild('tabPanel') public tabPanel: ElementRef<HTMLElement>;
  public twJoin = twJoin;
  constructor(
    private router: Router,
    private a11yService: A11yService
  ) {}
  public ngOnInit(): void {
    if (!this.activeTab()) {
      this.activeTab.set(this.configuration().tabs[0] || null);
    }
  }
  public selectTab(event: Event, tab: TabConfig): void {
    event.preventDefault();
    if (tab.routerLink) {
      this.router.navigate([tab.routerLink]);
    }
    this.activeTab.set(tab);
    this.tabChange.emit(tab);
  }
  public handleKeydown(event: KeyboardEvent, index: number) {
    const length = this.tabButtons.length;
    if (event.key === 'ArrowRight') {
      event.preventDefault(); // Prevent default scrolling behavior
      const nextIndex = (index + 1) % length;
      this.tabButtons.get(nextIndex)?.nativeElement.focus();
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault(); // Prevent default scrolling behavior
      const prevIndex = (index - 1 + length) % length;
      this.tabButtons.get(prevIndex)?.nativeElement.focus();
    } else if (event.key === 'Tab' && !event.shiftKey) {
      if (this.tabPanel.nativeElement?.children?.length) {
        event.preventDefault(); // Prevent default scrolling behavior
        this.a11yService.focusNextFocusable(this.tabPanel.nativeElement);
      }
    }
  }
}
